import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { fieldKey } from '../../fieldKeys';
import { optionValue, selectLabel, uiLang, useVocabulary, vocabLabels } from '../../kinds';
import { pathLabel, placeName } from '../../locations';
import { Button, SearchField, Skeleton, TextField } from '../../ui';
import {
  NO_PLACE, YEAR, decadesIn, normalizeDate, setFlag, setRange, toggleDecade, togglePlace, toggleValue,
} from './filters';
import { tagKey } from './fold';

/** Values listed before "Show N more". */
export const SHOWN = 10;
const SEARCH_LIMIT = 50;
const RANGE_PAUSE_MS = 600;

const cx = (...names) => names.filter(Boolean).join(' ');

/** "Seen", "read"…: the verbs of the collection's types, or the word for several. */
const verbKey = (verbs) => (verbs.length === 1 ? verbs[0] : 'mixed');

/** One checklist row: a checkbox, the label, the count (read out as "5 items"). */
function CheckRow({ label, count, checked, onChange, depth = 0, type = 'checkbox', name }) {
  const { t, i18n } = useTranslation();
  const number = (n) => new Intl.NumberFormat(i18n.resolvedLanguage || 'en').format(n);
  return (
    <label className={cx('facet-row', count === 0 && 'is-empty')} style={depth ? { paddingInlineStart: `${depth * 16}px` } : undefined}>
      <input type={type} name={name} checked={checked} onChange={onChange} />
      <span className="facet-row__label">{label}</span>
      {count !== null && count !== undefined && (
        <>
          <span className="facet-row__count" aria-hidden="true">{number(count)}</span>
          <span className="visually-hidden">{t('collection.filters.countA11y', { count })}</span>
        </>
      )}
    </label>
  );
}

/** A fieldset with its legend: one facet. */
function Facet({ legend, children, className }) {
  return (
    <fieldset className={cx('facet', className)}>
      <legend className="facet__legend">{legend}</legend>
      {children}
    </fieldset>
  );
}

/** A long list cut at SHOWN, with "Show N more" / "Show fewer". */
function CutList({ rows, extra = 0, onMore, renderRow }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const shown = open ? rows : rows.slice(0, SHOWN);
  const hidden = rows.length - shown.length;
  return (
    <>
      <div className="facet__rows">{shown.map(renderRow)}</div>
      {(hidden > 0 || open) && (
        <Button variant="ghost" size="sm" className="facet__more" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? t('collection.filters.showFewer') : t('collection.filters.showMore', { count: hidden })}
        </Button>
      )}
      {!open && hidden === 0 && extra > 0 && onMore && (
        <Button variant="ghost" size="sm" className="facet__more" aria-expanded={false} onClick={onMore}>
          {t('collection.filters.showMore', { count: extra })}
        </Button>
      )}
    </>
  );
}

/**
 * A tags facet past its top ten: the values from the server (…/facets/{key}/values),
 * searched inside the facet, counted like the panel.
 */
function TagSearch({ inventoryId, fieldName, facetKey, listParams, isSelected, onToggle, onClose }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [answer, setAnswer] = useState(null);
  const counter = useRef(0);
  useEffect(() => {
    const request = ++counter.current;
    const timer = setTimeout(() => {
      api.get(`/inventories/${inventoryId}/facets/${facetKey}/values`, {
        params: { ...listParams, search: search.trim() || undefined, limit: SEARCH_LIMIT },
        paramsSerializer: { indexes: null },
      }).then((response) => {
        if (request === counter.current) setAnswer(response.data);
      }).catch(() => {
        if (request === counter.current) setAnswer({ values: [], more: 0 });
      });
    }, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [inventoryId, facetKey, listParams, search]);
  return (
    <div className="facet__search">
      <SearchField label={t('collection.filters.searchIn', { name: fieldName })} placeholder={t('collection.filters.searchIn', { name: fieldName })}
                   value={search} onChange={setSearch} quiet autoFocus />
      <div className="facet__rows" aria-busy={answer === null}>
        {answer && answer.values.length === 0 && <p className="facet__none">{t('collection.filters.noValue')}</p>}
        {answer && answer.values.map((value) => (
          <CheckRow key={value.value} label={value.label || value.value} count={value.count}
                    checked={isSelected(value.value)} onChange={() => onToggle(value.value)} />
        ))}
      </div>
      {answer && answer.more > 0 && <p className="facet__none">{t('collection.filters.refine', { count: answer.more })}</p>}
      <Button variant="ghost" size="sm" className="facet__more" onClick={onClose}>{t('collection.filters.showFewer')}</Button>
    </div>
  );
}

/** A checklist of a field: options, codes, tags or yes/no. */
function ValuesFacet({ facet, field, filters, onChange, inventoryId, listParams }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const vocabulary = useVocabulary(field && field.type === 'vocab' ? field.vocabulary : null);
  const [searching, setSearching] = useState(false);
  const key = facet.key;
  const selected = filters.values[key] || [];
  const label = (entry) => {
    if (!field) return entry.label || entry.value;
    switch (field.type) {
      case 'yesno': return entry.value === '1' ? t('fieldInput.yes') : t('fieldInput.no');
      case 'vocab': return vocabLabels(vocabulary, [entry.value], lang, field)[0];
      case 'tags': return entry.label || entry.value;
      default: return String(selectLabel(field, entry.value, lang));
    }
  };
  // Options keep their own order; codes and tags come by count.
  let values = facet.values;
  if (field && ['select', 'radio', 'checkbox'].includes(field.type) && Array.isArray(field.options)) {
    const order = field.options.map(optionValue);
    const rank = (v) => { const i = order.indexOf(v); return i < 0 ? order.length : i; };
    values = [...values].sort((a, b) => rank(a.value) - rank(b.value));
  }
  if (field && field.type === 'yesno') values = [...values].sort((a, b) => Number(b.value) - Number(a.value));
  // A tag in the address as typed (a hand-made link) is the same filter as its folded value.
  const isTags = field && field.type === 'tags';
  const given = (value) => selected.filter((v) => v === value || (isTags && tagKey(v) === value));
  const toggle = (value) => {
    const present = given(value);
    onChange(present.length
      ? present.reduce((next, v) => toggleValue(next, key, v), filters)
      : toggleValue(filters, key, value), { push: true });
  };
  const name = field ? field.name : key;
  return (
    <Facet legend={name}>
      {searching ? (
        <TagSearch inventoryId={inventoryId} fieldName={name} facetKey={key} listParams={listParams}
                   isSelected={(value) => given(value).length > 0} onToggle={toggle} onClose={() => setSearching(false)} />
      ) : (
        <CutList
          rows={values}
          extra={facet.more || 0}
          onMore={() => setSearching(true)}
          renderRow={(entry) => (
            <CheckRow key={entry.value} label={label(entry)} count={entry.count}
                      checked={given(entry.value).length > 0} onChange={() => toggle(entry.value)} />
          )}
        />
      )}
    </Facet>
  );
}

/** YYYYMMDD (the server's number for a date) as YYYY-MM-DD. */
const dateText = (n) => {
  const s = String(n || '');
  if (!/^\d{8}$/.test(s)) return '';
  const [y, m, d] = [s.slice(0, 4), s.slice(4, 6), s.slice(6, 8)];
  return m === '00' ? y : d === '00' ? `${y}-${m}` : `${y}-${m}-${d}`;
};

/** From / to inputs, written to the address after a pause (replace), at once on Enter or leaving the field. */
function RangeInputs({ legendId, range, bounds, onCommit, kind = 'number', unit }) {
  const { t } = useTranslation();
  const [min, setMin] = useState(range?.min ?? '');
  const [max, setMax] = useState(range?.max ?? '');
  const sent = useRef({ min: range?.min ?? '', max: range?.max ?? '' });
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  // Changed elsewhere (a chip removed, Back): shown here.
  useEffect(() => {
    const next = { min: range?.min ?? '', max: range?.max ?? '' };
    if (next.min !== sent.current.min || next.max !== sent.current.max) {
      sent.current = next;
      setMin(next.min);
      setMax(next.max);
    }
  }, [range?.min, range?.max]);
  const isDate = kind === 'date';
  const hintId = useId();
  const inputId = useId();
  // A date is typed as text (YYYY, YYYY-MM or YYYY-MM-DD) and written normalised; an unreadable one waits.
  const clean = (v) => (isDate ? normalizeDate(v) : String(v).trim());
  const commit = (raw) => {
    const values = { min: clean(raw.min), max: clean(raw.max) };
    if (values.min === null || values.max === null) return;
    if (values.min === sent.current.min && values.max === sent.current.max) return;
    sent.current = values;
    commitRef.current(values);
  };
  useEffect(() => {
    const timer = setTimeout(() => commit({ min, max }), RANGE_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [min, max]); // eslint-disable-line react-hooks/exhaustive-deps
  const now = () => {
    if (isDate) {
      const [a, b] = [normalizeDate(min), normalizeDate(max)];
      if (a !== null) setMin(a);
      if (b !== null) setMax(b);
    }
    commit({ min, max });
  };
  const shown = (v) => (isDate ? dateText(v) : v);
  const input = (bound, value, set, placeholder) => {
    const invalid = isDate && clean(value) === null;
    const id = `${inputId}-${bound}`;
    return (
    <TextField
      id={id}
      label={`${bound === 'min' ? t('collection.filters.from') : t('collection.filters.to')}${unit ? ` (${unit})` : ''}`}
      type={isDate ? 'text' : 'number'}
      inputMode={isDate ? 'numeric' : 'decimal'}
      step={isDate ? undefined : 'any'}
      pattern={isDate ? '\\d{4}(-\\d{1,2}(-\\d{1,2})?)?' : undefined}
      value={value}
      placeholder={placeholder !== null && placeholder !== undefined ? String(shown(placeholder)) : undefined}
      error={invalid ? t('collection.filters.dateInvalid') : undefined}
      aria-describedby={[legendId, isDate ? hintId : null, invalid ? `${id}-error` : null].filter(Boolean).join(' ')}
      onChange={(e) => set(e.target.value)}
      onBlur={now}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); now(); } }}
      className="facet-range__input"
    />
    );
  };
  return (
    <>
      <div className="facet-range">
        {input('min', min, setMin, bounds?.min)}
        {input('max', max, setMax, bounds?.max)}
      </div>
      {isDate && <p id={hintId} className="ui-field__hint facet-range__hint">{t('collection.filters.dateHint')}</p>}
    </>
  );
}

function RangeFacet({ facet, field, filters, onChange }) {
  const legendId = useId();
  const unit = field && field.type === 'measure' ? (field.units || [])[0]
    : field && field.type === 'money' ? (field.currency || 'EUR') : undefined;
  return (
    <Facet legend={<span id={legendId}>{field ? field.name : facet.key}</span>}>
      <RangeInputs legendId={legendId} range={filters.ranges[facet.key]} bounds={{ min: facet.min, max: facet.max }}
                   kind={field && field.type === 'date' ? 'date' : 'number'} unit={unit}
                   onCommit={(range) => onChange(setRange(filters, facet.key, range), { push: false })} />
    </Facet>
  );
}

function YearFacet({ facet, filters, onChange }) {
  const { t } = useTranslation();
  const legendId = useId();
  const range = filters.ranges[YEAR];
  const decades = facet.values.map((v) => parseInt(v.value, 10));
  const ticked = decadesIn(range, decades);
  return (
    <Facet legend={<span id={legendId}>{t('collection.filters.year')}</span>}>
      <CutList
        rows={facet.values}
        renderRow={(entry) => {
          const decade = parseInt(entry.value, 10);
          return (
            <CheckRow key={entry.value} label={t('collection.filters.decade', { decade })} count={entry.count}
                      checked={ticked.includes(decade)}
                      onChange={() => onChange(setRange(filters, YEAR, toggleDecade(range, decade, decades)), { push: true })} />
          );
        }}
      />
      <RangeInputs legendId={legendId} range={range} bounds={{ min: facet.min, max: facet.max }}
                   onCommit={(next) => onChange(setRange(filters, YEAR, next), { push: false })} />
    </Facet>
  );
}

function PlaceFacet({ facet, filters, onChange, places }) {
  const { t } = useTranslation();
  const byId = useMemo(() => new Map((places || []).map((p) => [String(p.id), p])), [places]);
  const rows = facet.values.map((entry) => ({ ...entry, place: byId.get(entry.value) })).filter((row) => row.place);
  if (facet.none > 0 || facet.none_selected) rows.push({ value: NO_PLACE, count: facet.none, none: true });
  const toggle = (value) => onChange(togglePlace(filters, value), { push: true });
  return (
    <Facet legend={t('collection.filters.place')}>
      <CutList
        rows={rows}
        renderRow={(row) => (
          <CheckRow key={row.value} count={row.count} depth={row.none ? 0 : row.place.depth}
                    label={row.none ? t('collection.filters.noPlace') : <span title={pathLabel(t, row.place.path)}>{placeName(t, row.place)}</span>}
                    checked={filters.places.includes(row.value)} onChange={() => toggle(row.value)} />
        )}
      />
    </Facet>
  );
}

/** Two exclusive choices (seen / not yet, rated / not): ticking one drops the other. */
/**
 * Exclusive choices (seen / not yet; rated / not; an average threshold): a
 * radio group, the fieldset its name, with "All" to drop the filter.
 */
function ChoiceFacet({ legend, name, options, value, allCount = null, filters, onChange }) {
  const { t } = useTranslation();
  const group = useId();
  const pick = (next) => onChange(setFlag(filters, name, next), { push: true });
  return (
    <Facet legend={legend}>
      <div className="facet__rows">
        <CheckRow type="radio" name={group} label={t('collection.filters.all')} count={allCount}
                  checked={value === null} onChange={() => pick(null)} />
        {options.map((option) => (
          <CheckRow key={option.value} type="radio" name={group} label={option.label} count={option.count}
                    checked={value === option.value} onChange={() => pick(option.value)} />
        ))}
      </div>
    </Facet>
  );
}

function FlagFacet({ facet, legend, labels, name, filters, onChange }) {
  const counts = Object.fromEntries(facet.values.map((v) => [v.value, v.count]));
  return (
    <ChoiceFacet legend={legend} name={name} value={filters[name]} filters={filters} onChange={onChange}
                 allCount={(counts['1'] ?? 0) + (counts['0'] ?? 0)}
                 options={['1', '0'].map((value) => ({ value, label: labels[value], count: counts[value] ?? 0 }))} />
  );
}

/**
 * The filters beside the list (phase 3 §3.1): the caller's own flags, the
 * fields shown as filters (checklists and ranges), the year and the places,
 * each with its counts under the other filters. The type stays in the chips
 * of the toolbar. Every change is a new address (push for a tick, replace
 * while a range is typed), back on page 1.
 */
function FilterPanel({ facets, loading, filters, onChange, fieldsByKey, personal, inventoryId, listParams, places, className }) {
  const { t } = useTranslation();
  if (!facets) {
    return (
      <div className={cx('filter-panel', className)} aria-busy="true">
        {[0, 1, 2].map((i) => <Skeleton key={i} height={88} radius="var(--radius-md)" />)}
      </div>
    );
  }
  const verb = verbKey(personal.verbs || []);
  const verbs = (personal.verbs || []).map((v) => t(`collection.filters.verb.${v}`));
  const doneLegend = t('collection.filters.byMe', { verbs: verbs.length ? verbs.join(' / ') : t('collection.filters.verb.seen') });
  const sections = facets.map((facet) => {
    switch (facet.key) {
      case '@type': return null;   // the toolbar's chips
      case '@done':
        return (
          <FlagFacet key="@done" facet={facet} name="done" filters={filters} onChange={onChange}
                     legend={doneLegend.charAt(0).toUpperCase() + doneLegend.slice(1)}
                     labels={{ 1: t(`collection.filters.doneYes.${verb}`), 0: t(`collection.filters.doneNo.${verb}`) }} />
        );
      case '@rated':
        return (
          <FlagFacet key="@rated" facet={facet} name="rated" filters={filters} onChange={onChange}
                     legend={t('collection.filters.rated')}
                     labels={{ 1: t('collection.filters.ratedYes'), 0: t('collection.filters.ratedNo') }} />
        );
      case '@avg':
        return (
          <ChoiceFacet key="@avg" legend={t('collection.filters.average')} name="avg" value={filters.avg} filters={filters} onChange={onChange}
                       options={facet.values.map((entry) => ({
                         value: entry.value, label: t('collection.filters.atLeast', { count: Number(entry.value) }), count: entry.count,
                       }))} />
        );
      case YEAR: return <YearFacet key={YEAR} facet={facet} filters={filters} onChange={onChange} />;
      case '@place': return <PlaceFacet key="@place" facet={facet} filters={filters} onChange={onChange} places={places} />;
      default: {
        const field = fieldsByKey[facet.key];
        if (facet.kind === 'range') return <RangeFacet key={facet.key} facet={facet} field={field} filters={filters} onChange={onChange} />;
        if (facet.kind !== 'values') return null;
        if (facet.values.length === 0 && !(facet.more > 0)) return null;
        return <ValuesFacet key={facet.key} facet={facet} field={field} filters={filters} onChange={onChange}
                            inventoryId={inventoryId} listParams={listParams} />;
      }
    }
  }).filter(Boolean);
  return (
    <div className={cx('filter-panel', loading && 'is-fetching', className)} aria-busy={loading || undefined}>
      {sections.length ? sections : <p className="facet__none">{t('collection.filters.nothing')}</p>}
    </div>
  );
}

/** The fields of the collection's types by key, the default type's first (as the server merges them). */
export const fieldsByKeyOf = (inventory, itemTypes) => {
  const ordered = [...(itemTypes || [])].sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
  const out = {};
  [...ordered.flatMap((type) => type.fields || []), ...(inventory.fields || [])].forEach((field) => {
    const key = fieldKey(field);
    if (!(key in out)) out[key] = field;
  });
  return out;
};

export default FilterPanel;
